import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaUsers } from "react-icons/fa";
import { SERVER_URL } from "../../config";
import { Field, inputClass, SearchableSelect } from "./Shared";
import { currencyOptions, licenceOptions, statusOptions, subscriptionMonthOptions, computeTotalAmount, formatMoney } from "./utils";

const UserFormModal = ({
    open,
    editingUser,
    selectedServer,
    form,
    setForm,
    formError,
    saving,
    onCancel,
    onSubmit,
}) => {
    const [rateLoading, setRateLoading] = useState(false);
    const [rateNotice, setRateNotice] = useState("");
    const [employees, setEmployees] = useState([]);
    const [regions, setRegions] = useState([]);

    // Auto-fetch rate whenever licence type changes and we know the provider
    useEffect(() => {
        const provider = selectedServer?.provider_name;
        if (!open || !provider || !form.licence_type) return;

        let cancelled = false;
        setRateLoading(true);
        setRateNotice("");

        axios
            .get(`${SERVER_URL}/Cloud/getProviderRate.php`, {
                params: { provider_name: provider, licence_type: form.licence_type },
            })
            .then((res) => {
                if (cancelled) return;
                if (res.data.success) {
                    setForm((prev) => ({
                        ...prev,
                        rate: res.data.data.rate,
                        currency: prev.currency || res.data.data.currency,
                    }));
                    setRateNotice(`Rate auto-filled from ${provider}'s rate card. You can still change it.`);
                } else {
                    setRateNotice(res.data.message || "No rate card found — enter the rate manually.");
                }
            })
            .catch(() => {
                if (!cancelled) setRateNotice("Could not fetch rate automatically — enter it manually.");
            })
            .finally(() => {
                if (!cancelled) setRateLoading(false);
            });

        return () => { cancelled = true; };
    }, [form.licence_type, selectedServer?.provider_name, open, setForm]);

    // Sell person / region dropdown data
    useEffect(() => {
        if (!open) return;
        axios
            .get(`${SERVER_URL}/Cloud/getEmployees.php`)
            .then((res) => {
                if (res.data.success) {
                    setEmployees(res.data.employees || []);
                    setRegions(res.data.regions || []);
                }
            })
            .catch(() => {});
    }, [open]);

    if (!open) return null;

    const sellPersonOptions = employees.map((e) => ({ label: e.label, value: e.employee_id }));
    const regionOptions = regions.map((r) => ({ label: r, value: r }));

    const totalAmount = computeTotalAmount(form.rate, form.subscription, form.discount, 1);

    const onSellPersonChange = (employeeId) => {
        const emp = employees.find((e) => e.employee_id === employeeId);
        setForm({
            ...form,
            sell_person_id: employeeId,
            sell_person_name: emp ? emp.employee_name : "",
            region: form.region || (emp ? emp.region || "" : ""),
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white border border-slate-200 p-6" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <FaUsers className="w-5 h-5 text-blue-600" />
                        {editingUser ? "Edit User" : "Add New User"} {selectedServer ? `— ${selectedServer.server_name}` : ""}
                    </h2>
                    <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">✕</button>
                </div>

                {formError && <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-4">{formError}</p>}

                {!editingUser && (
                    <p className="text-[11px] text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
                        User ID is generated automatically once you save.
                    </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Company Name - Required */}
                    <Field label="Company Name" required>
                        <input
                            type="text"
                            value={form.company_name}
                            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                            className={inputClass}
                            placeholder="Enter company name"
                        />
                    </Field>

                    {/* Username - Required */}
                    <Field label="Username" required>
                        <input
                            type="text"
                            value={form.user_name}
                            onChange={(e) => setForm({ ...form, user_name: e.target.value })}
                            className={inputClass}
                            placeholder="Enter username"
                        />
                    </Field>

                    {/* Password - Required */}
                    <Field label={editingUser ? "New Password (Optional)" : "Password"} required={!editingUser}>
                        <input
                            type="password"
                            placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
                            value={form.user_password}
                            onChange={(e) => setForm({ ...form, user_password: e.target.value })}
                            className={inputClass}
                        />
                    </Field>

                    <Field label="Contact Person">
                        <input
                            type="text"
                            value={form.contact_person_name}
                            onChange={(e) => setForm({ ...form, contact_person_name: e.target.value })}
                            className={inputClass}
                            placeholder="Contact person name"
                        />
                    </Field>

                    <Field label="Contact Number">
                        <input
                            type="text"
                            value={form.contact_person_number}
                            onChange={(e) => setForm({ ...form, contact_person_number: e.target.value })}
                            className={inputClass}
                            placeholder="Contact number"
                        />
                    </Field>

                    <Field label="Licence Type">
                        <select
                            value={form.licence_type}
                            onChange={(e) => setForm({ ...form, licence_type: e.target.value })}
                            className={inputClass}
                        >
                            <option value="">Select Licence Type</option>
                            {licenceOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Server Port" required>
                        <input
                            type="number"
                            value={form.server_port}
                            onChange={(e) => setForm({ ...form, server_port: e.target.value })}
                            className={inputClass}
                            placeholder="Server port number"
                        />
                    </Field>

                    {form.licence_type === "shared" && (
                        <>
                            <Field label="Actual Username">
                                <input
                                    type="text"
                                    value={form.actual_user_name}
                                    onChange={(e) => setForm({ ...form, actual_user_name: e.target.value })}
                                    className={inputClass}
                                    placeholder="Actual username"
                                />
                            </Field>
                            <Field label="Actual Password" hint={editingUser ? "Leave blank to keep the current password" : undefined}>
                                <input
                                    type="password"
                                    value={form.actual_user_password}
                                    onChange={(e) => setForm({ ...form, actual_user_password: e.target.value })}
                                    className={inputClass}
                                    placeholder={editingUser ? "Leave blank to keep current" : "Actual password"}
                                />
                            </Field>
                        </>
                    )}

                    <Field label="Activation Date">
                        <input
                            type="date"
                            value={form.activation_date || ""}
                            onChange={(e) => setForm({ ...form, activation_date: e.target.value })}
                            className={inputClass}
                        />
                    </Field>

                    <Field label="Expire Date">
                        <input
                            type="date"
                            value={form.expire_date || ""}
                            onChange={(e) => setForm({ ...form, expire_date: e.target.value })}
                            className={inputClass}
                        />
                    </Field>

                    <Field label="Next Renewal Date">
                        <input
                            type="date"
                            value={form.next_renewal_date || ""}
                            onChange={(e) => setForm({ ...form, next_renewal_date: e.target.value })}
                            className={inputClass}
                        />
                    </Field>

                    <Field label="Prorata Expire Date">
                        <input
                            type="date"
                            value={form.prorata_expire_date || ""}
                            onChange={(e) => setForm({ ...form, prorata_expire_date: e.target.value })}
                            className={inputClass}
                        />
                    </Field>

                    <Field label="Rate" hint={rateLoading ? "Fetching rate…" : rateNotice}>
                        <input
                            type="number"
                            value={form.rate}
                            onChange={(e) => setForm({ ...form, rate: e.target.value })}
                            className={inputClass}
                            placeholder="Rate amount"
                        />
                    </Field>

                    <Field label="Discount">
                        <input
                            type="number"
                            value={form.discount}
                            onChange={(e) => setForm({ ...form, discount: e.target.value })}
                            className={inputClass}
                            placeholder="0"
                        />
                    </Field>

                    <Field label="Currency">
                        <select
                            value={form.currency}
                            onChange={(e) => setForm({ ...form, currency: e.target.value })}
                            className={inputClass}
                        >
                            <option value="">Select Currency</option>
                            {currencyOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Subscription">
                        <SearchableSelect
                            options={subscriptionMonthOptions}
                            value={form.subscription}
                            onChange={(v) => setForm({ ...form, subscription: v })}
                            placeholder="Search months…"
                        />
                    </Field>

                    <Field label="Sell Person" hint="Search by name or employee ID">
                        <SearchableSelect
                            options={sellPersonOptions}
                            value={form.sell_person_id}
                            onChange={onSellPersonChange}
                            placeholder="Search employee…"
                        />
                    </Field>

                    <Field label="Region">
                        <SearchableSelect
                            options={regionOptions}
                            value={form.region}
                            onChange={(v) => setForm({ ...form, region: v })}
                            placeholder="Search region…"
                            allowCustom
                        />
                    </Field>

                    <Field label="Gateway Path">
                        <input
                            type="text"
                            value={form.gateway_path}
                            onChange={(e) => setForm({ ...form, gateway_path: e.target.value })}
                            className={inputClass}
                            placeholder="Gateway path"
                        />
                    </Field>

                    <Field label="Status">
                        <select
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                            className={inputClass}
                        >
                            {statusOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Referred Person">
                        <input
                            type="text"
                            value={form.referred_person}
                            onChange={(e) => setForm({ ...form, referred_person: e.target.value })}
                            className={inputClass}
                            placeholder="Referred person name"
                        />
                    </Field>

                    <Field label="Referred Person Number">
                        <input
                            type="text"
                            value={form.referred_person_number}
                            onChange={(e) => setForm({ ...form, referred_person_number: e.target.value })}
                            className={inputClass}
                            placeholder="Referred person number"
                        />
                    </Field>

                    <Field label="Remarks" className="col-span-1 md:col-span-2">
                        <textarea
                            value={form.remarks}
                            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                            className={inputClass}
                            rows="2"
                            placeholder="Additional remarks"
                        />
                    </Field>

                    <Field label="Reason" className="col-span-1 md:col-span-2">
                        <textarea
                            value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            className={inputClass}
                            rows="2"
                            placeholder="Reason for status change"
                        />
                    </Field>
                </div>

                <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-center justify-between">
                    <div>
                        <div className="text-xs font-semibold text-blue-700">Total Amount</div>
                        <div className="text-[11px] text-blue-500">rate × subscription − discount</div>
                    </div>
                    <div className="text-xl font-extrabold text-blue-800">{formatMoney(totalAmount, form.currency)}</div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg text-sm text-white disabled:opacity-60 transition-opacity"
                        style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}
                    >
                        {saving ? "Saving…" : editingUser ? "Update" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserFormModal;